import React from "react";
import { motion } from "framer-motion";

/**
 * StoreBackground — the "combo" variant is now the locked-in look:
 * circuits + subtle grid + 2 breathing orbs + IC chip motif + grain.
 * (Circuit / stars / grid variants + the ?bg= switcher were removed
 *  after user confirmed combo on 2026-09-10.)
 */
export default function StoreBackground({ children, theme }) {
  return (
    <>
      <ComboBg theme={theme} />
      {children}
    </>
  );
}

/**
 * theme: undefined | "mt5" | "ctrader" — shifts orb + circuit tints.
 *   default : cyan + yellow  (neutral hub)
 *   mt5     : blue  + yellow (MetaTrader palette)
 *   ctrader : green + red    (cTrader palette)
 */
function ComboBg({ theme }) {
  const palette = theme === "mt5"
    ? { orb1: "rgba(56,189,248,0.13)", orb2: "rgba(251,191,36,0.10)", trace1: "#38BDF8", trace2: "#FBBF24", trace3: "#93C5FD", chip: "#38BDF8" }
    : theme === "ctrader"
    ? { orb1: "rgba(46,202,127,0.13)", orb2: "rgba(248,113,113,0.10)", trace1: "#2ECA7F", trace2: "#F87171", trace3: "#86EFAC", chip: "#2ECA7F" }
    : { orb1: "rgba(6,182,212,0.09)",  orb2: "rgba(250,204,21,0.07)",  trace1: "#D6FF38", trace2: "#22D3EE", trace3: "#E879F9", chip: "#22D3EE" };
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-[#04060A] transition-colors duration-700">
      {/* subtle base grid */}
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{
          backgroundImage:
            "linear-gradient(#2D3748 1px, transparent 1px), linear-gradient(90deg, #2D3748 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />
      {/* two soft breathing orbs — tinted by theme */}
      <motion.div
        className="absolute -top-32 -left-20 w-[520px] h-[520px] rounded-full blur-3xl transition-colors duration-700"
        style={{ background: palette.orb1 }}
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute bottom-0 -right-20 w-[560px] h-[560px] rounded-full blur-3xl transition-colors duration-700"
        style={{ background: palette.orb2 }}
        animate={{ scale: [1, 1.12, 1] }}
        transition={{ duration: 24, repeat: Infinity, ease: "easeInOut", delay: 5 }}
      />
      {/* circuit traces overlay */}
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full opacity-70">
        <defs>
          <path id="cbt-1" d="M 5 20 L 25 20 L 25 40 L 55 40 L 55 60 L 90 60" fill="none" />
          <path id="cbt-2" d="M 10 75 L 30 75 L 30 55 L 60 55 L 60 85 L 92 85" fill="none" />
          <path id="cbt-3" d="M 85 8 L 85 30 L 65 30 L 65 50 L 40 50 L 40 92" fill="none" />
        </defs>
        {[palette.trace1, palette.trace2, palette.trace3].map((c, i) => (
          <g key={i}>
            <use href={`#cbt-${i + 1}`} stroke={c} strokeOpacity="0.15" strokeWidth="0.2" />
            <circle r="0.4" fill={c}>
              <animateMotion dur={`${10 + i * 2}s`} repeatCount="indefinite" begin={`${i * 1.3}s`}>
                <mpath href={`#cbt-${i + 1}`} />
              </animateMotion>
            </circle>
          </g>
        ))}
      </svg>
      {/* corner IC chip */}
      <svg className="absolute bottom-12 left-6 w-24 h-24 opacity-25" viewBox="0 0 100 100">
        <rect x="20" y="20" width="60" height="60" fill="none" stroke={palette.chip} strokeWidth="1" strokeOpacity="0.5" />
        {[...Array(8)].map((_, i) => (
          <React.Fragment key={i}>
            <line x1={20 + i * 8} y1="10" x2={20 + i * 8} y2="20" stroke={palette.chip} strokeOpacity="0.6" strokeWidth="0.8" />
            <line x1={20 + i * 8} y1="80" x2={20 + i * 8} y2="90" stroke={palette.chip} strokeOpacity="0.6" strokeWidth="0.8" />
            <line x1="10" y1={20 + i * 8} x2="20" y2={20 + i * 8} stroke={palette.chip} strokeOpacity="0.6" strokeWidth="0.8" />
            <line x1="80" y1={20 + i * 8} x2="90" y2={20 + i * 8} stroke={palette.chip} strokeOpacity="0.6" strokeWidth="0.8" />
          </React.Fragment>
        ))}
        <circle cx="30" cy="30" r="2" fill={palette.chip} fillOpacity="0.9" />
      </svg>
      {/* grain */}
      <div
        className="absolute inset-0 opacity-[0.03] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='120' height='120' filter='url(%23n)' opacity='0.55'/></svg>\")",
          backgroundSize: "220px 220px",
        }}
      />
      <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse at center, transparent 35%, rgba(4,6,10,0.6) 92%)" }} />
    </div>
  );
}
